// Wireframe dlgEditModel — modal de edição de modelo LLM, markup LITERAL
// (varredura v2 §5.6-C3): WfModalShell (.dl-h/.dl-b/.dl-f) + .wf-field/
// .wf-label/.wf-input/.wf-select/.wf-textarea + .nc-2col. Campos: provedor
// (disabled) · custos entrada/saída (USD/1M) · descrição · reasoning
// (habilitado + nível) · Ativo via OAuth · Ativo via API Key → PATCH.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { api, type CanonicalEffort, type LlmModel } from '@/lib/api';
import { WfModalShell } from '@/components/settings/WfModalShell';
import { useToast } from '@/components/ui/Toast';
import { EFFORT_LEVELS, authFlags, hasReasoning, mutationErrorMessage } from './modelHelpers';

// Wireframe (dlgEditModel) lista minimal/low/medium/high/max — sem 'xhigh'.
// Mantemos a lista do wireframe, mas se o nível atual do modelo for 'xhigh'
// caímos na lista canônica completa pra não perder dado existente.
const WIREFRAME_LEVELS: CanonicalEffort[] = ['minimal', 'low', 'medium', 'high', 'max'];

interface ModelEditDialogProps {
  model: LlmModel | null;
  providerName: string;
  onClose: () => void;
}

export function ModelEditDialog({ model, providerName, onClose }: ModelEditDialogProps) {
  if (!model) return null;
  // key remonta o form a cada modelo aberto (estado inicial vem do modelo).
  return <ModelEditForm key={model.id} model={model} providerName={providerName} onClose={onClose} />;
}

function ModelEditForm({ model, providerName, onClose }: { model: LlmModel; providerName: string; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [inputCost, setInputCost] = useState(
    model.input_cost_per_m != null ? String(model.input_cost_per_m) : ''
  );
  const [outputCost, setOutputCost] = useState(
    model.output_cost_per_m != null ? String(model.output_cost_per_m) : ''
  );
  const [notes, setNotes] = useState(model.notes ?? '');
  const [reasoningEnabled, setReasoningEnabled] = useState(hasReasoning(model));
  const [effortLevel, setEffortLevel] = useState<CanonicalEffort>(
    model.default_effort_level ?? model.supported_effort_levels?.[0] ?? 'medium'
  );
  const initialFlags = authFlags(model);
  const [viaOauth, setViaOauth] = useState(initialFlags.oauth);
  const [viaApiKey, setViaApiKey] = useState(initialFlags.apiKey);

  const mutation = useMutation({
    mutationFn: (payload: Partial<LlmModel>) => api.updateLlmModel(model.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-catalog'] });
      toast({ message: `Modelo ${model.model_key} atualizado.`, type: 'success' });
      onClose();
    },
    onError: (err) => {
      toast({ message: mutationErrorMessage(err, 'atualizar o modelo'), type: 'error' });
    },
  });

  const levelOptions = (
    WIREFRAME_LEVELS.includes(effortLevel) ? WIREFRAME_LEVELS : EFFORT_LEVELS
  ).map((lvl) => ({ value: lvl, label: lvl }));

  const handleSave = () => {
    const parsedIn = inputCost.trim() === '' ? null : Number(inputCost);
    const parsedOut = outputCost.trim() === '' ? null : Number(outputCost);
    if ((parsedIn != null && Number.isNaN(parsedIn)) || (parsedOut != null && Number.isNaN(parsedOut))) {
      toast({ message: 'Custos devem ser numéricos (USD / 1M tokens).', type: 'warning' });
      return;
    }

    // Reasoning desabilitado → zera níveis; habilitado → garante o nível escolhido
    // dentro dos suportados e usa como default.
    const existing = model.supported_effort_levels ?? [];
    const supported: CanonicalEffort[] = reasoningEnabled
      ? existing.includes(effortLevel)
        ? existing
        : [...existing, effortLevel].sort((a, b) => EFFORT_LEVELS.indexOf(a) - EFFORT_LEVELS.indexOf(b))
      : [];

    mutation.mutate({
      input_cost_per_m: parsedIn,
      output_cost_per_m: parsedOut,
      notes: notes.trim() || null,
      supported_effort_levels: supported,
      default_effort_level: reasoningEnabled ? effortLevel : null,
      available_via_oauth: viaOauth,
      available_via_api_key: viaApiKey,
    });
  };

  return (
    <WfModalShell
      open
      onClose={onClose}
      icon={<SlidersHorizontal className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden />}
      title={
        <>
          Editar <span className="wf-row-code" style={{ fontSize: '.85em' }}>{model.model_key}</span>
        </>
      }
      footer={
        <>
          <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </button>
          <button type="button" className="wf-btn wf-btn-primary" onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="wf-field">
        <label className="wf-label">Provedor</label>
        <input className="wf-input" value={providerName} disabled />
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Custo token entrada (USD / 1M)</label>
          <input
            className="wf-input"
            inputMode="decimal"
            value={inputCost}
            onChange={(e) => setInputCost(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label">Custo token saída (USD / 1M)</label>
          <input
            className="wf-input"
            inputMode="decimal"
            value={outputCost}
            onChange={(e) => setOutputCost(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="wf-field">
        <label className="wf-label">Descrição curta</label>
        <textarea
          className="wf-textarea"
          style={{ minHeight: 64 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Para que serve este modelo"
        />
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Reasoning</label>
          <select
            className="wf-select"
            value={reasoningEnabled ? 'on' : 'off'}
            onChange={(e) => setReasoningEnabled(e.target.value === 'on')}
          >
            <option value="on">Habilitado</option>
            <option value="off">Desabilitado</option>
          </select>
        </div>
        <div className="wf-field">
          <label className="wf-label">Nível de reasoning</label>
          <select
            className="wf-select"
            value={effortLevel}
            onChange={(e) => setEffortLevel(e.target.value as CanonicalEffort)}
            disabled={!reasoningEnabled}
          >
            {levelOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Ativo via OAuth</label>
          <select
            className="wf-select"
            value={viaOauth ? 'yes' : 'no'}
            onChange={(e) => setViaOauth(e.target.value === 'yes')}
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
        <div className="wf-field">
          <label className="wf-label">Ativo via API Key</label>
          <select
            className="wf-select"
            value={viaApiKey ? 'yes' : 'no'}
            onChange={(e) => setViaApiKey(e.target.value === 'yes')}
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
      </div>
    </WfModalShell>
  );
}
